import type { JSX, MouseEvent } from 'react';

import { cn } from '../lib/cn.js';
import { libelleDeMention, segmentsDeTexte, type EntreeDeMention, type SegmentDeTexte } from './mentions.js';

/**
 * L'EVENEMENT QU'UNE MENTION CLIQUEE EMET (vague du soir, essai, ecart 1).
 * C'est le meme que celui du shell (`packages/shell/src/centre/ouvrirCentre.ts`),
 * ecrit une seconde fois parce que `@taki/ui` ne depend pas du shell : la
 * fenetre de communication l'ecoute et ouvre le message direct avec la
 * personne, ou Lia. Le format exact est dans le contrat du soir.
 */
export const EVENEMENT_OUVRIR_CENTRE = 'taki:centre:ouvrir';

/** Ce qu'une mention cliquee demande : le direct avec cette personne, ou Lia. */
export type OuvrirDepuisMention = { source: 'directs'; personId: string } | { source: 'lia' };

/**
 * La demande d'une mention, ou `null` si elle n'en fait pas : `@tous` n'ouvre
 * rien, sa propre mention non plus, un compte inconnu de la source non plus.
 * Exporte pour le test.
 */
export function demandeDeMention(
  segment: Extract<SegmentDeTexte, { type: 'mention' }>,
  moi: string | null | undefined,
  entrees: readonly EntreeDeMention[],
): OuvrirDepuisMention | null {
  if (segment.genre === 'lia') return { source: 'lia' };
  if (segment.genre !== 'personne' || segment.id === moi) return null;
  return entrees.some((e) => e.genre === 'personne' && e.id === segment.id) ? { source: 'directs', personId: segment.id } : null;
}

/** Emet la demande : la fenetre ouvre le direct avec la personne, ou Lia. */
export function ouvrirDepuisMention(demande: OuvrirDepuisMention): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OuvrirDepuisMention>(EVENEMENT_OUVRIR_CENTRE, { detail: demande }));
}

/**
 * UN TEXTE STOCKE RENDU EN PASTILLES : `@[p-celia]` devient une pastille
 * « @Celia », `@tous` et `@Lia` leurs pastilles. Un personId inconnu de la
 * source devient « @Compte inconnu » : un identifiant ne s'affiche jamais.
 *
 * UNE MENTION SE COMPORTE COMME UN LIEN (R2) : la pastille d'une autre personne
 * ou de Lia est un BOUTON (curseur de lien, focus clavier, Entree ou clic) qui
 * ouvre le message direct avec la personne, ou Lia, dans la fenetre de
 * communication. `@tous` et sa propre mention restent des pastilles inertes.
 */
export function TexteAvecMentions({
  texte,
  entrees,
  moi,
  className,
}: {
  texte: string;
  entrees: readonly EntreeDeMention[];
  /** Le personId de la personne connectee : sa propre mention n'est pas un lien. */
  moi?: string | null | undefined;
  className?: string;
}): JSX.Element {
  const segments = segmentsDeTexte(texte);
  return <span className={cn('whitespace-pre-wrap break-words', className)}>
    {segments.map((segment, index) => {
      if (segment.type === 'texte') return <span key={index}>{segment.valeur}</span>;
      const libelle = libelleDeMention(segment, entrees);
      const detail = entrees.find((e) => e.id === segment.id)?.detail;
      const couleur = 'text-mention-ink';
      const demande = demandeDeMention(segment, moi, entrees);
      if (demande === null) {
        return <span key={index} data-mention={segment.genre} data-mention-id={segment.id} title={detail ?? libelle}
          className={cn('inline font-semibold', couleur)}>{libelle}</span>;
      }
      const titre = demande.source === 'lia' ? 'Ouvrir Lia' : `Écrire à ${libelle.slice(1)}`;
      return <button key={index} type="button" data-mention={segment.genre} data-mention-id={segment.id} data-mention-lien
        title={detail ? `${titre} (${detail})` : titre} aria-label={titre}
        onClick={(event: MouseEvent<HTMLButtonElement>) => { event.preventDefault(); event.stopPropagation(); ouvrirDepuisMention(demande); }}
        className={cn('inline cursor-pointer rounded-xs align-baseline font-semibold decoration-1 underline-offset-4 hover:underline focus-visible:shadow-ring focus-visible:outline-none', couleur)}>
        {libelle}
      </button>;
    })}
  </span>;
}
